/**
 * web/src/components/UpgradeSheet.tsx — the Pear Plus paywall (card 32BEnNO6I2kx).
 *
 * Opens itself when `api()` sees a 402 plus_required — one listener at the
 * app root, so every gated tap in every screen gets the same sheet without
 * any of them importing billing. It also opens on demand (Settings' plan row,
 * the upsell CTA) via the exported `announcePlusRequired()`.
 *
 * Two prices, one couple: $9.99/month or $79.99/year (Jonathan's decision,
 * 2026-10-10). Both buttons go to Stripe Hosted Checkout — Pear never sees a
 * card number. Prices come from /api/billing/state so the sheet cannot drift
 * from what Stripe charges; the fallback constants only render before that
 * call lands.
 */
import { useEffect, useState } from 'react';
import { Overlay } from '../ui/Overlay';
import {
  FALLBACK_PRICES,
  PLUS_EVENT,
  PLUS_FEATURES,
  fetchBillingState,
  startCheckout,
  startPortal,
  type BillingPrices,
} from '../lib/billing';

interface Props {
  /** Optional: the app root listens for the event and passes nothing. */
  open?: boolean;
  onClose?: () => void;
}

export function UpgradeSheet({ open: controlledOpen, onClose }: Props = {}) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlledOpen ?? uncontrolled;
  const [prices, setPrices] = useState<BillingPrices>(FALLBACK_PRICES);
  const [isPlus, setIsPlus] = useState(false);
  const [trialing, setTrialing] = useState(false);
  /** Plus that was GIVEN not bought (card Z7SGpA59WHSb) — no portal to open. */
  const [granted, setGranted] = useState(false);
  const [busy, setBusy] = useState<'month' | 'year' | 'portal' | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = () => setUncontrolled(true);
    window.addEventListener(PLUS_EVENT, handler);
    return () => window.removeEventListener(PLUS_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    setError('');
    let cancelled = false;
    void fetchBillingState()
      .then((s) => {
        if (cancelled) return;
        setPrices(s.prices ?? FALLBACK_PRICES);
        // Plus WITHOUT a subscription is either the sign-up trial (card
        // ImIQwJDLW6Xs) or a promo grant (card Z7SGpA59WHSb). Neither has a
        // Stripe customer, so "Manage subscription" would open a portal that
        // 404s. Both are people to CONVERT, so both get the prices instead.
        const isTrialing = Boolean(s.entitlement.trialing);
        const isGranted = Boolean(s.entitlement.promoCode) && !s.entitlement.subscriptionId;
        setTrialing(isTrialing);
        setGranted(isGranted);
        setIsPlus(Boolean(s.entitlement.plus) && !isTrialing && !isGranted);
      })
      .catch(() => {
        /* the fallback prices are honest enough for an in-flight sheet */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const close = () => {
    setUncontrolled(false);
    onClose?.();
  };

  const go = async (interval: 'month' | 'year' | 'portal') => {
    setError('');
    setBusy(interval);
    try {
      const url = interval === 'portal' ? await startPortal() : await startCheckout(interval);
      // The browser leaves for Stripe; nothing else to do here.
      window.location.assign(url);
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setError(
        msg.includes('stripe_not_configured')
          ? 'Billing Is Not Switched On for This Server Yet.'
          : 'Could Not Start Checkout — Try Again in a Moment.',
      );
      setBusy(null);
    }
  };

  return (
    <Overlay
      open={open}
      onClose={close}
      className="more-overlay up-overlay"
      panelClassName="more-sheet up-sheet"
      labelledBy="upgrade-title"
      testId="upgrade-sheet"
    >
      <button className="more-close up-close" onClick={close} aria-label="Close">×</button>
      <h2 id="upgrade-title" className="up-title">
        {isPlus || granted ? 'Pear Plus is on' : trialing ? 'Your free trial is on' : 'Pear Plus'}
      </h2>
      {isPlus ? (
        <p className="up-note">
          One subscription covers you both. Manage it any time from Settings.
        </p>
      ) : (
        <>
          <p className="up-note">
            {granted
              ? 'Granted to you — nothing to pay, and no subscription to manage. Pick a plan to keep everything on once the grant ends.'
              : trialing
                ? 'Your trial runs for 30 days. Pick a plan to keep everything on when it ends — one subscription covers you both.'
                : 'One subscription for you both — the concierge services and the weekly briefing.'}
          </p>
          <ul className="up-features">
            {PLUS_FEATURES.map((f) => (
              <li key={f.key}>
                <span className="up-tick" aria-hidden="true">✓</span>
                {f.label}
              </li>
            ))}
          </ul>
          <div className="up-prices">
            <button
              className="pear-btn"
              data-testid="upgrade-monthly"
              disabled={busy !== null}
              onClick={() => void go('month')}
            >
              {busy === 'month' ? 'Opening…' : prices.month.label}
            </button>
            <button
              className="pear-btn pear-btn-blush"
              data-testid="upgrade-yearly"
              disabled={busy !== null}
              onClick={() => void go('year')}
            >
              {busy === 'year' ? 'Opening…' : `${prices.year.label} — best value`}
            </button>
          </div>
          <p className="up-fineprint">Cancel any time. Essentials stays free.</p>
        </>
      )}
      {isPlus && (
        <div className="up-prices">
          <button className="pear-btn" disabled={busy !== null} onClick={() => void go('portal')}>
            {busy === 'portal' ? 'Opening…' : 'Manage subscription'}
          </button>
        </div>
      )}
      {error && <p className="up-error" role="alert">{error}</p>}
    </Overlay>
  );
}
